import { useState, useRef, useEffect } from 'react';
import { Plus, Minus, Trash2, Edit3, Search, X, Package, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api/apiClient';
import { useRepProfile } from '@/lib/RepProfileContext';
import { ProductCard, AddProductSheet } from '@/components/orders/ProductPicker';
import { cur } from '@/lib/currency';

function fmt(n, d = 2) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: d, maximumFractionDigits: d });
}

// Product picker bottom sheet
function ProductSheet({ editLines, onPick, onClose }) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [imageMap, setImageMap] = useState({});
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const imageTimer = useRef(null);

  const fetch = async (q) => {
    setLoading(true);
    // Prefer the SellGo endpoint so reps only see products in their
    // warehouse + dealer categories. Fallback chain matches the other
    // product-listing screens.
    const params = { search: q || undefined, limit: 40 };
    let res;
    try {
      res = await api.get('/sellgo_inventory', params);
    } catch {
      try {
        res = await api.get('/sellgo-products', params);
      } catch {
        res = await api.get('/products', params);
      }
    }
    if (res?.success) {
      const data = res.data || [];
      setProducts(data);
      // Lazy-load images (same as the create-order product picker).
      clearTimeout(imageTimer.current);
      imageTimer.current = setTimeout(() => {
        const missing = data.map(p => p.id).filter(id => !(id in imageMap));
        if (missing.length) {
          api.post('/products/images', { product_ids: missing.slice(0, 40) })
            .then(r => { if (r?.success) setImageMap(prev => ({ ...prev, ...r.data })); });
        }
      }, 300);
    }
    setLoading(false);
  };

  useEffect(() => { fetch(''); }, []);

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetch(v), 350);
  };

  const displayed = products.map(p => ({ ...p, _imgSrc: imageMap[p.id] || null }));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end" onClick={onClose}>
      <div className="bg-card w-full rounded-t-3xl p-5 space-y-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground text-lg">إضافة صنف</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => handleSearch(e.target.value)} placeholder="بحث في المنتجات..." className="pr-10 rounded-xl" autoFocus />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-center text-sm text-muted-foreground py-8">جاري التحميل...</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {displayed.map(p => {
                const inCart = editLines.find(l => l.product_id === p.id || l.template_id === p.id);
                return (
                  <ProductCard
                    key={p.id}
                    product={p}
                    inCart={inCart}
                    price={p.list_price}
                    onTap={onPick}
                  />
                );
              })}
            </div>
          )}
        </div>
        <button onClick={onClose} className="w-full py-3 bg-primary text-white rounded-xl font-semibold">تم</button>
      </div>
    </div>
  );
}

export default function OrderLinesTab({ order, editMode, editLines, setEditLines, editNotes, setEditNotes, setDirty }) {
  const { profile: repProfile, isManager } = useRepProfile();
  const allowEditPrice = isManager || !!repProfile.allow_edit_price;
  const [editingPrice, setEditingPrice] = useState(null);
  const [showProductSheet, setShowProductSheet] = useState(false);
  const [addingProduct, setAddingProduct] = useState(null);

  const lines = order.lines || [];
  const isDraft = order.state === 'draft';

  // Add or update a line from the shared AddProductSheet — identical UX to the
  // create-order flow. Matches by product to preserve an existing line's id/isNew.
  const upsertLine = ({ product, quantity, price_unit, discount }) => {
    setEditLines(ls => {
      const idx = ls.findIndex(l => l.product_id === product.id || l.template_id === product.id);
      if (idx >= 0) {
        return ls.map((l, i) => i === idx
          ? { ...l, quantity, price_unit, discount: discount || 0 }
          : l);
      }
      return [...ls, {
        id: null, template_id: product.id, product_id: product.id,
        name: product.name, default_code: product.default_code,
        quantity, price_unit, discount: discount || 0, isNew: true,
      }];
    });
    setDirty(true);
    setAddingProduct(null);
  };

  const updateDiscount = (idx, val) => {
    let d = parseFloat(val);
    if (isNaN(d) || d < 0) d = 0;
    if (d > 100) d = 100;
    setEditLines(ls => ls.map((l, i) => i === idx ? { ...l, discount: d } : l));
    setDirty(true);
  };

  const updateQty = (idx, delta) => {
    setEditLines(ls => ls.map((l, i) => i === idx ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l));
    setDirty(true);
  };

  const setQtyDirect = (idx, val) => {
    const qty = parseFloat(val);
    if (!isNaN(qty) && qty > 0) {
      setEditLines(ls => ls.map((l, i) => i === idx ? { ...l, quantity: qty } : l));
      setDirty(true);
    }
  };

  const updatePrice = (idx, val) => {
    const price = parseFloat(val);
    setEditLines(ls => ls.map((l, i) => i === idx ? { ...l, price_unit: isNaN(price) ? 0 : price } : l));
    setDirty(true);
  };

  const removeLine = (idx) => { setEditLines(ls => ls.filter((_, i) => i !== idx)); setDirty(true); };

  const displayLines = editMode ? editLines : lines;

  // Edit-mode totals (estimate, mirrors the new-order review): gross − discount.
  const editGross = editLines.reduce((s, l) => s + l.quantity * l.price_unit, 0);
  const editDisc = editLines.reduce((s, l) => s + l.quantity * l.price_unit * (l.discount || 0) / 100, 0);
  const editAfter = editGross - editDisc;

  const fSubtotal = editMode ? editGross : (order.amount_untaxed || 0);
  const fDiscount = editMode ? editDisc : 0;
  const tax = editMode ? 0 : (order.amount_tax || 0);
  const total = editMode ? editAfter : (order.amount_total || 0);

  return (
    <div className="space-y-3">
      {/* Lines list */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-foreground text-sm">الأصناف</span>
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{displayLines.length}</span>
          </div>
          {editMode && (
            <button onClick={() => setShowProductSheet(true)} className="flex items-center gap-1 text-xs text-primary font-bold">
              <Plus className="w-3.5 h-3.5" /> إضافة
            </button>
          )}
        </div>

        {/* Lines */}
        {displayLines.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-muted-foreground">لا توجد أصناف</p>
            {editMode && (
              <button onClick={() => setShowProductSheet(true)} className="text-primary text-sm font-semibold mt-2 flex items-center gap-1 mx-auto">
                <Plus className="w-4 h-4" /> إضافة صنف
              </button>
            )}
          </div>
        ) : editMode ? (
          <div className="divide-y divide-border">
            {editLines.map((line, idx) => (
              <div key={idx} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm font-semibold text-foreground flex-1 leading-snug line-clamp-2">{line.name}</p>
                  </div>
                  <button onClick={() => removeLine(idx)} className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(idx, -1)} className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <input type="number" value={line.quantity} onChange={e => setQtyDirect(idx, e.target.value)}
                      className="w-14 h-8 text-center text-sm border border-border rounded-lg bg-card focus:outline-none focus:ring-1 focus:ring-primary/50" min={0.01} />
                    <button onClick={() => updateQty(idx, 1)} className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                      <Plus className="w-3 h-3 text-white" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">سعر:</span>
                    {editingPrice === idx && allowEditPrice ? (
                      <input type="number" value={line.price_unit} onChange={e => updatePrice(idx, e.target.value)}
                        onBlur={() => setEditingPrice(null)} className="w-20 h-7 px-2 text-sm border border-primary rounded-lg bg-card focus:outline-none" autoFocus min={0} />
                    ) : (
                      <button
                        onClick={() => allowEditPrice && setEditingPrice(idx)}
                        disabled={!allowEditPrice}
                        className={`flex items-center gap-0.5 text-sm font-medium ${
                          allowEditPrice ? 'hover:text-primary' : 'cursor-not-allowed text-muted-foreground'
                        }`}>
                        {fmt(line.price_unit)}
                        {allowEditPrice
                          ? <Edit3 className="w-3 h-3 text-muted-foreground" />
                          : <Lock className="w-3 h-3 text-muted-foreground/60" />}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">خصم:</span>
                    <input type="number" value={line.discount || 0} onChange={e => updateDiscount(idx, e.target.value)}
                      className="w-14 h-7 text-center text-xs border border-border rounded-lg bg-card focus:outline-none focus:ring-1 focus:ring-primary/50" min={0} max={100} />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                  <span className="font-bold text-primary text-sm whitespace-nowrap mr-auto">
                    {fmt(line.quantity * line.price_unit * (1 - (line.discount || 0) / 100))} {cur()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {lines.map(line => {
              const name = Array.isArray(line.product_id) ? line.product_id[1] : (line.name || '');
              return (
                <div key={line.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-muted-foreground/40" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground line-clamp-1">{name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {fmt(line.product_uom_qty, 0)} × {fmt(line.price_unit)} {cur()}
                      {line.discount > 0 && <span className="text-red-500 mr-1"> (خصم {line.discount}%)</span>}
                    </p>
                  </div>
                  <p className="font-bold text-primary text-sm whitespace-nowrap">{fmt(line.price_subtotal)} {cur()}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals footer */}
        <div className="border-t border-border bg-muted/20 px-4 py-3 space-y-1.5">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>المجموع الفرعي</span><span>{fmt(fSubtotal)} {cur()}</span>
          </div>
          {fDiscount > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>الخصم</span><span className="text-red-600">-{fmt(fDiscount)} {cur()}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>الضريبة{editMode ? ' (14%)' : ''}</span><span>{fmt(tax)} {cur()}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-foreground border-t border-border pt-1.5 mt-1">
            <span>الإجمالي</span>
            <span className="text-primary text-base">{fmt(total)} {cur()}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {editMode ? (
        <div>
          <p className="text-xs text-muted-foreground font-medium mb-1.5">ملاحظات</p>
          <Textarea value={editNotes} onChange={e => { setEditNotes(e.target.value); setDirty(true); }}
            placeholder="ملاحظات الطلب..." className="rounded-xl" rows={3} />
        </div>
      ) : order.note ? (
        <div className="bg-card border border-border rounded-2xl p-4">
          <p className="text-xs text-muted-foreground mb-1">ملاحظات</p>
          <p className="text-sm text-foreground">{order.note}</p>
        </div>
      ) : null}

      {showProductSheet && (
        <ProductSheet editLines={editLines} onPick={setAddingProduct} onClose={() => setShowProductSheet(false)} />
      )}
      {addingProduct && (() => {
        const existing = editLines.find(l => l.product_id === addingProduct.id || l.template_id === addingProduct.id);
        return (
          <AddProductSheet
            product={addingProduct}
            price={existing?.price_unit ?? addingProduct.list_price}
            existing={existing}
            onAdd={upsertLine}
            onClose={() => setAddingProduct(null)}
            allowEditPrice={allowEditPrice}
          />
        );
      })()}
    </div>
  );
}